// ── Arithmetic evaluator ─────────────────────────────────────────────────
// The calculator used to evaluate its display string with
// `new Function('return ' + expr)()`. That is eval() in everything but name:
// it compiles a string at runtime, which forces `script-src 'unsafe-eval'`
// into any Content-Security-Policy worth shipping, and it makes behaviour
// depend on whatever the JavaScript parser happens to do with a half-typed
// expression.
//
// Only digits, `.`, `+ - * /` and parentheses are ever accepted here. The
// grammar is deliberately the simple one a hand calculator implies — no
// unary-plus-in-the-middle, no exponent notation, no identifiers — because
// anything richer would only give an attacker more surface.
//
// Returns a number, or NaN when the input is not one complete expression.
// Division follows IEEE-754 (`1/0` -> `Infinity`), matching the old
// implementation exactly so no existing calculation changes result.
const evaluateArithmetic = (input) => {
  if (typeof input !== 'string') return NaN;
  const src = input.replace(/\s+/g, '');
  if (!src) return NaN;

  // No key on this keypad produces `++` or `--`: to subtract a negative you
  // write `5-(-3)`. JavaScript reads them as increment/decrement, so the old
  // `new Function` implementation threw and silently skipped the update.
  // Rejecting here reproduces that, and it also means an accidental double-tap
  // of `+`/`-` can never push a doubled amount (typing `100--50` for `100-50`)
  // into the field this calculator is writing to.
  if (/\+\+|--/.test(src)) return NaN;

  let i = 0;

  const peek = () => src[i];

  const parseNumber = () => {
    const start = i;
    let dots = 0;
    while (i < src.length && ((src[i] >= '0' && src[i] <= '9') || src[i] === '.')) {
      if (src[i] === '.') dots += 1;
      i += 1;
    }
    if (i === start || dots > 1) return NaN;
    const value = Number(src.slice(start, i));
    return Number.isNaN(value) ? NaN : value;
  };

  // '(' expr ')' | number
  const parsePrimary = () => {
    if (peek() === '(') {
      i += 1;
      const inner = parseAdditive();
      if (Number.isNaN(inner) || peek() !== ')') return NaN;
      i += 1;
      return inner;
    }
    return parseNumber();
  };

  // '-' unary | '+' unary | primary
  const parseUnary = () => {
    if (peek() === '-') { i += 1; const v = parseUnary(); return Number.isNaN(v) ? NaN : -v; }
    if (peek() === '+') { i += 1; return parseUnary(); }
    return parsePrimary();
  };

  // unary (('*' | '/') unary)*
  const parseMultiplicative = () => {
    let left = parseUnary();
    while (peek() === '*' || peek() === '/') {
      const op = src[i];
      i += 1;
      const right = parseUnary();
      if (Number.isNaN(left) || Number.isNaN(right)) return NaN;
      left = op === '*' ? left * right : left / right;
    }
    return left;
  };

  // multiplicative (('+' | '-') multiplicative)*
  const parseAdditive = () => {
    let left = parseMultiplicative();
    while (peek() === '+' || peek() === '-') {
      const op = src[i];
      i += 1;
      const right = parseMultiplicative();
      if (Number.isNaN(left) || Number.isNaN(right)) return NaN;
      left = op === '+' ? left + right : left - right;
    }
    return left;
  };

  const value = parseAdditive();
  // Trailing characters mean the string was not one expression (`2+`,
  // `1..2`, `2 3`) — reject rather than silently evaluate a prefix.
  return i === src.length ? value : NaN;
};

export { evaluateArithmetic };
